"use client";

// Learn showcase — alternating text/preview rows that slide in from each side.
// Each row pairs a short value block with a real screen recording of the live
// Learn page (Study, Exam Studio, Knowledge Bank, Progress), played via the
// shared ClipVideo (poster + looping MP4). Clips are cropped to the app content
// (no browser scrollbar / gray padding) and versioned (-v2) to bust caches. The
// CSS-mock components below are kept as a fallback (exported via LEARN_MOCKS).
// Built on motion/react.

import { useRef } from "react";
import { motion, useInView } from "motion/react";
import { ClipVideo } from "@/components/marketing/clip-video";
import {
  Sparkles,
  ShieldCheck,
  BookOpen,
  Activity,
  Check,
  Clock,
  GraduationCap,
  Flag,
} from "lucide-react";

/* ────────── Page mocks ────────── */

const FRAME =
  "lift-card relative overflow-hidden rounded-2xl border hairline-strong bg-card " +
  "shadow-[0_24px_48px_-24px_hsl(var(--foreground)/0.18)]";

function Chrome({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-1.5 px-3 h-7 border-b hairline bg-foreground/[0.02]">
      {[0, 1, 2].map((i) => (
        <span key={i} className="h-2 w-2 rounded-full bg-foreground/15" aria-hidden />
      ))}
      <span className="ml-2 font-mono text-[10px] tracking-[0.14em] uppercase text-foreground/45">
        {label}
      </span>
    </div>
  );
}

/* A real screen recording of a live page, in the same window-chrome frame as
 * the CSS mocks. Poster + looping MP4 via the shared ClipVideo. */
function ClipFrame({ src, label }: { src: string; label: string }) {
  return (
    <div className={FRAME}>
      <Chrome label={label} />
      <ClipVideo src={src} />
    </div>
  );
}

/* Study — file + AI tool tabs + a generated summary, scoped to relevant pages. */
function StudyPreview() {
  const tabs = ["Read", "Summary", "Quiz", "Flashcards", "True/false", "Memory", "Podcast"];
  return (
    <div className={FRAME}>
      <Chrome label="learn.askfinz.ai · study" />
      <div className="px-4 pt-3.5 pb-4">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[13px] font-medium tracking-tight truncate">
              Lecture 1 — Introduction.pptx
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-foreground/45">
              File · 124 slides
            </div>
          </div>
          <span className="shrink-0 inline-flex items-center gap-1.5 rounded-full border hairline-strong px-2.5 py-1 text-[10px] font-mono text-foreground/65">
            Pages 23–124
          </span>
        </div>

        <div className="mt-3 flex flex-wrap gap-1.5">
          {tabs.map((t) => (
            <span
              key={t}
              className={
                "rounded-md px-2 py-1 text-[11px] " +
                (t === "Summary"
                  ? "bg-foreground text-background"
                  : "border hairline text-foreground/65")
              }
            >
              {t}
            </span>
          ))}
        </div>

        <div className="mt-3.5 rounded-xl border hairline bg-foreground/[0.015] p-3.5">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] text-foreground/45">
            <Sparkles className="h-3 w-3" /> Summary · grounded in your slides
          </div>
          <div className="mt-2.5 text-[12.5px] leading-relaxed text-foreground/80">
            This lecture introduces the integration of urban planning and transport,
            focusing on how land use and mobility shape one another.
          </div>
          <ul className="mt-2.5 space-y-1.5">
            {[
              "Land use and transportation are intrinsically linked",
              "Historical shift from pre-industrial to post-Fordist cities",
              "Travel-demand modelling supports planning decisions",
            ].map((b) => (
              <li key={b} className="flex items-start gap-2 text-[12px] text-foreground/70">
                <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-foreground/45" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

/* Exam Studio — the lockdown room: question, options, live timer + integrity. */
function ExamPreview() {
  const opts = [
    { k: "A", t: "Transportation planning" },
    { k: "B", t: "Urban planning and design" },
    { k: "C", t: "Integration of planning and transport", on: true },
    { k: "D", t: "Real-estate development" },
  ];
  return (
    <div className={FRAME}>
      <Chrome label="secure exam · locked" />
      <div className="px-4 pt-3 pb-4">
        <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.14em] text-foreground/45">
          <span>Q1 / 4 · Multiple choice</span>
          <span className="inline-flex items-center gap-1.5 text-foreground/65">
            <Clock className="h-3 w-3" /> 89:56 · server
          </span>
        </div>
        <div className="mt-2 text-[13px] font-medium leading-snug">
          What is the primary focus of the course “Built Environment &amp; Smart Mobility”?
        </div>
        <div className="mt-3 space-y-1.5">
          {opts.map((o) => (
            <div
              key={o.k}
              className={
                "flex items-center gap-2.5 rounded-lg border px-3 py-2 text-[12px] " +
                (o.on
                  ? "hairline-strong bg-foreground/[0.04] text-foreground"
                  : "hairline text-foreground/70")
              }
            >
              <span
                className={
                  "inline-flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-mono " +
                  (o.on ? "bg-foreground text-background" : "border hairline text-foreground/55")
                }
              >
                {o.k}
              </span>
              <span>{o.t}</span>
              {o.on && <Check className="ml-auto h-3.5 w-3.5 text-foreground/70" />}
            </div>
          ))}
        </div>
        <div className="mt-3 flex items-center gap-3 border-t hairline pt-2.5 text-[10px] font-mono text-foreground/55">
          <span className="inline-flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--accent))] animate-pulse" />
            Focus monitored
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Flag className="h-3 w-3" /> Integrity log · 2 flags
          </span>
          <span className="ml-auto">AI-graded on submit</span>
        </div>
      </div>
    </div>
  );
}

/* Knowledge Bank — everything you've made, per file and course-wide. */
function BankPreview() {
  const rows = [
    {
      title: "All course content",
      chips: ["Summary 1", "Concepts 1", "Flashcards 5", "True/false 5", "Podcast 10"],
    },
    {
      title: "Lecture 1 — Introduction.pptx",
      chips: ["Quiz 4", "Flashcards 10", "Memory 10", "Podcast 3"],
    },
  ];
  return (
    <div className={FRAME}>
      <Chrome label="learn.askfinz.ai · knowledge-bank" />
      <div className="px-4 pt-3.5 pb-4">
        <div className="flex items-center gap-2 text-[13px] font-medium tracking-tight">
          <BookOpen className="h-4 w-4 text-foreground/65" /> Knowledge Bank
        </div>
        <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-foreground/45">
          Everything you&#39;ve saved
        </div>
        <div className="mt-3 space-y-2.5">
          {rows.map((r) => (
            <div key={r.title} className="rounded-xl border hairline bg-foreground/[0.015] p-3">
              <div className="text-[12.5px] font-medium">{r.title}</div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {r.chips.map((c) => (
                  <span
                    key={c}
                    className="rounded-full border hairline-strong bg-card px-2 py-0.5 text-[10.5px] font-mono text-foreground/65"
                  >
                    {c}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* Progress — exam-driven analytics: KPIs, topic mastery, readiness. */
function ProgressPreview() {
  const kpis = [
    { l: "Latest", v: "86%" },
    { l: "Best", v: "86%" },
    { l: "Readiness", v: "81%" },
  ];
  const topics = [
    { t: "Introduction", p: 100 },
    { t: "Urban mobility", p: 100 },
    { t: "Course content", p: 40 },
  ];
  return (
    <div className={FRAME}>
      <Chrome label="learn.askfinz.ai · progress" />
      <div className="px-4 pt-3.5 pb-4">
        <div className="flex items-center gap-2 text-[13px] font-medium tracking-tight">
          <Activity className="h-4 w-4 text-foreground/65" /> My progress
        </div>
        <div className="mt-3 grid grid-cols-3 gap-2">
          {kpis.map((k) => (
            <div key={k.l} className="rounded-xl border hairline bg-foreground/[0.015] px-3 py-2.5">
              <div className="font-mono text-[9px] uppercase tracking-[0.14em] text-foreground/45">
                {k.l}
              </div>
              <div className="mt-0.5 font-serif text-[20px] leading-none">{k.v}</div>
            </div>
          ))}
        </div>
        <div className="mt-3.5">
          <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-foreground/45">
            Topic mastery
          </div>
          <div className="mt-2 space-y-2">
            {topics.map((t) => (
              <div key={t.t} className="flex items-center gap-2.5">
                <span className="w-24 shrink-0 text-[11.5px] text-foreground/70 truncate">{t.t}</span>
                <span className="h-2 flex-1 overflow-hidden rounded-full bg-foreground/[0.08]">
                  <span
                    className="block h-full rounded-full bg-[hsl(var(--accent))]"
                    style={{ width: `${t.p}%` }}
                  />
                </span>
                <span className="w-9 shrink-0 text-right font-mono text-[10.5px] text-foreground/55">
                  {t.p}%
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ────────── Rows ────────── */

type Row = {
  icon: typeof Sparkles;
  name: string;
  tagline: string;
  bullets: string[];
  preview: React.ReactNode;
};

const ROWS: Row[] = [
  {
    icon: Sparkles,
    name: "Study",
    tagline: "Every study tool, from your slides.",
    bullets: [
      "Summaries, key concepts, quizzes, flashcards, true/false, a memory game and narrated mini-podcasts.",
      "Grounded in your own lecture — not the open internet.",
      "Saved once, so nothing regenerates. Scope any tool to the pages that matter.",
    ],
    preview: <ClipFrame src="/learn/gifs/learn-study-v5.mp4" label="learn.askfinz.ai · study" />,
  },
  {
    icon: ShieldCheck,
    name: "Exam Studio",
    tagline: "A mock exam that writes itself.",
    bullets: [
      "Timed, server-graded exams generated from your course.",
      "Honest browser-level proctoring — fullscreen, focus, second-display and a live integrity log.",
      "An AI debrief on every attempt, grounded in the material.",
    ],
    preview: <ClipFrame src="/learn/gifs/learn-exam-montage-v5.mp4" label="secure exam · studio" />,
  },
  {
    icon: BookOpen,
    name: "Knowledge Bank",
    tagline: "Everything you make, kept together.",
    bullets: [
      "Summaries, decks, podcasts and every exam question with its answer.",
      "Organised per file and across the whole course.",
      "Reused to build your next quiz and exam.",
    ],
    preview: <ClipFrame src="/learn/gifs/learn-bank-v5.mp4" label="learn.askfinz.ai · knowledge-bank" />,
  },
  {
    icon: Activity,
    name: "Progress",
    tagline: "Know your gaps before the exam.",
    bullets: [
      "Score trend, per-topic mastery and an exam-readiness gauge.",
      "A personal study plan pointing you at your weakest topics.",
      "Durable and cross-device — built from your real attempts.",
    ],
    preview: <ClipFrame src="/learn/gifs/learn-progress-montage-v5.mp4" label="learn.askfinz.ai · progress" />,
  },
];

function FeatureRow({ row, index }: { row: Row; index: number }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const reversed = index % 2 === 1;
  const Icon = row.icon;
  const words = row.tagline.split(" ");

  return (
    <div ref={ref} className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center">
      {/* Text */}
      <motion.div
        initial={{ opacity: 0, x: reversed ? 40 : -40 }}
        animate={inView ? { opacity: 1, x: 0 } : {}}
        transition={{ duration: 0.55, ease: "easeOut" }}
        className={reversed ? "lg:order-2" : ""}
      >
        <div className="flex items-center gap-2">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg border hairline-strong text-foreground/80">
            <Icon className="h-4 w-4" />
          </span>
          <span className="kicker">{row.name}</span>
        </div>
        <h3 className="mt-4 text-2xl md:text-3xl font-medium tracking-tight">
          {words.slice(0, -2).join(" ")}{" "}
          <span className="font-serif italic">{words.slice(-2).join(" ")}</span>
        </h3>
        <ul className="mt-5 space-y-3">
          {row.bullets.map((b) => (
            <li key={b} className="flex items-start gap-2.5 text-foreground/70">
              <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-foreground text-background">
                <Check className="h-3 w-3" />
              </span>
              <span className="text-sm leading-relaxed">{b}</span>
            </li>
          ))}
        </ul>
      </motion.div>

      {/* Preview mock — slides in from the opposite side */}
      <motion.div
        initial={{ opacity: 0, x: reversed ? -40 : 40 }}
        animate={inView ? { opacity: 1, x: 0 } : {}}
        transition={{ duration: 0.55, ease: "easeOut", delay: 0.08 }}
        className={reversed ? "lg:order-1" : ""}
      >
        {row.preview}
      </motion.div>
    </div>
  );
}

export function LearnFeaturesAlternating() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section className="section bg-foreground/[0.015] border-y hairline">
      <div className="container-x">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 16 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="max-w-2xl"
        >
          <p className="eyebrow text-accent">The workspace, end to end</p>
          <h2 className="display-text mt-3 text-[clamp(1.8rem,3.6vw,2.8rem)] tracking-tight">
            One course, every way to <span className="font-serif italic">learn it.</span>
          </h2>
          <p className="mt-3 text-foreground/65 text-base md:text-lg">
            Study tools, a lockdown exam simulator, a knowledge bank and honest progress —
            each grounded in your own material, all under one login.
          </p>
        </motion.div>

        <div className="mt-12 space-y-16 md:space-y-20">
          {ROWS.map((row, i) => (
            <FeatureRow key={row.name} row={row} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

export const LEARN_MOCKS = { StudyPreview, ExamPreview, BankPreview, ProgressPreview };
